import { TrackingKeys } from '@/hotdesk-ui/constants/tracking-keys';
import type { UserBookingStatsForTracking } from '@/hotdesk-ui/services/booking-stats-for-tracking.service';
import type { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { User, UserSubscriptionReadOnly } from 'hotdesk_typescript';

export type UserTrackingProperties = Record<string, string | number | boolean>;

export type BuildUserTrackingPropertiesOptions = {
    languageCode: string;
    geo?: GeoCodeResult | null;
    /** Closest workspace from listing API (`loc` search); not stored on `user.meta`. */
    nearestWorkspace?: string;
};

const formatValidityDate = (iso: string | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const formatJoined = (dateJoined: Date | string | undefined): string => {
    if (dateJoined == null) return '';
    const d = dateJoined instanceof Date ? dateJoined : new Date(dateJoined);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString();
};

const metaString = (
    meta: Record<string, unknown>,
    ...keys: string[]
): string => {
    for (const key of keys) {
        const v = meta[key];
        if (v == null) continue;
        if (typeof v === 'string') return v;
        if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    }
    return '';
};

const resolveBrowserTz = (): string => {
    try {
        const parts = new Intl.DateTimeFormat('en', {
            timeZoneName: 'short'
        }).formatToParts(new Date());
        const short = parts.find((p) => p.type === 'timeZoneName')?.value;
        if (short) return short;
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    } catch {
        return '';
    }
};

const resolveIsHost = (user: User): boolean => {
    const meta = user.meta as Record<string, unknown> | undefined;
    if (meta?.is_host === true || meta?.is_host === 'true') return true;
    if (meta?.isHost === true || meta?.isHost === 'true') return true;
    const ownerFlag = (user as User & { isOwner?: boolean }).isOwner;
    if (ownerFlag === true) return true;
    return user.isStaff === true;
};

const activeSubscription = (
    user: User
): UserSubscriptionReadOnly | undefined => {
    const subs = user.userSubscriptions ?? [];
    return (
        subs.find((s) => s.isActive && s.isValid) ??
        subs.find((s) => s.isActive) ??
        subs[0]
    );
};

const passStats = (
    promo: UserSubscriptionReadOnly['hotdeskPromo'] | undefined
): { consumed: number; limit: number } => {
    if (!promo) return { consumed: 0, limit: 0 };
    const limit = promo.userLimit ?? 0;
    const remaining = promo.remainingUsage ?? 0;
    const consumed = Math.max(0, limit - remaining);
    return { consumed, limit };
};

/**
 * Builds the PostHog person profile map (Flutter `userTrackingProperties`).
 */
export function buildUserTrackingProperties(
    user: User,
    stats: UserBookingStatsForTracking,
    options: BuildUserTrackingPropertiesOptions
): UserTrackingProperties {
    const { languageCode, geo, nearestWorkspace: nearestFromApi } = options;
    const meta = (user.meta ?? {}) as Record<string, unknown>;

    const first = (user.firstName ?? '').trim();
    const last = (user.lastName ?? '').trim();
    const displayName = [first, last].filter(Boolean).join(' ');

    const isHost = resolveIsHost(user);
    const accountType = isHost ? 'host' : 'hotdesker';

    const sub = activeSubscription(user);
    const subscriptionModel = sub?.subscription?.alias ?? '';
    const isSubscribed = !!(sub?.isActive && sub?.isValid);

    const desk = passStats(sub?.hotdeskPromo);
    const meeting = passStats(sub?.meetingRoomPromo);

    const companyGroupId =
        user.companyGroups?.map((g) => g.id).filter((id) => id != null).join(',') ??
        metaString(meta, 'company_group_id', 'companyGroupId');

    const city =
        geo?.city?.trim() ||
        metaString(meta, 'city', 'City') ||
        '';
    const country =
        geo?.country?.trim() ||
        metaString(meta, 'country', 'Country') ||
        '';

    let location = '';
    if (geo?.lat != null && geo?.log != null) {
        location = JSON.stringify({
            latitude: geo.lat,
            longitude: geo.log
        });
    } else {
        const metaLoc = meta.location ?? meta.Location;
        if (metaLoc != null) {
            location =
                typeof metaLoc === 'string'
                    ? metaLoc
                    : JSON.stringify(metaLoc);
        }
    }

    const nearestWorkspace =
        nearestFromApi?.trim() ||
        metaString(
            meta,
            'nearest_workspace',
            'nearestWorkspace',
            'Nearest Workspace'
        );

    const msgEmail =
        metaString(meta, 'msg-email', 'msg_email') || 'true';
    const msgPush =
        metaString(meta, 'msg-push', 'msg_push') || 'false';
    const msgSms =
        metaString(meta, 'msg-sms', 'msg_sms') || 'true';

    const totalBookings = stats.totalBookings ?? 0;

    return {
        name: displayName,
        first_name: first,
        last_name: last,
        email: user.email ?? '',
        [TrackingKeys.kIdentity]: String(user.id ?? ''),
        [TrackingKeys.kCreatedAt]: formatJoined(user.dateJoined),
        [TrackingKeys.kAccountType]: accountType,
        [TrackingKeys.kIsHost]: isHost,
        [TrackingKeys.kPhone]: user.phoneNumber ?? '',
        [TrackingKeys.kCompanyGroupId]: companyGroupId,
        [TrackingKeys.kLanguage]: languageCode,
        [TrackingKeys.kPlanType]: subscriptionModel,
        [TrackingKeys.kSubscriptionModel]: subscriptionModel,
        [TrackingKeys.kSubscriptionStatus]: isSubscribed ? 'active' : 'inactive',
        [TrackingKeys.kSubscriptionPackage]: sub?.package?.name ?? '',
        [TrackingKeys.kSubscriptionPackageValidity]: formatValidityDate(
            sub?.validUntil
        ),
        [TrackingKeys.kDeskPassesConsumed]: desk.consumed,
        [TrackingKeys.kDeskPassesLimit]: desk.limit,
        [TrackingKeys.kMeetingRoomPassesConsumed]: meeting.consumed,
        [TrackingKeys.kMeetingRoomPassesLimit]: meeting.limit,
        [TrackingKeys.kIsFirstBookingCompleted]: !stats.isFirstBooking,
        [TrackingKeys.kTotalBookings]: totalBookings,
        [TrackingKeys.kFirstBookingDate]: stats.firstBookingDate ?? '',
        [TrackingKeys.kLastBookingDate]: stats.lastBookingDate ?? '',
        [TrackingKeys.kCity]: city,
        [TrackingKeys.kCountry]: country,
        [TrackingKeys.kLocation]: location,
        [TrackingKeys.kTz]: resolveBrowserTz(),
        [TrackingKeys.kNearestWorkspace]: nearestWorkspace,
        [TrackingKeys.kMsgEmail]: msgEmail,
        [TrackingKeys.kMsgPush]: msgPush,
        [TrackingKeys.kMsgSms]: msgSms
    };
}
